/* Mix & Match v4.1 — JS
   - Gated flow: Step1 (purpose) -> Step2 (categories) -> Step3 (gift addons -> products)
   - Category multi-select + “Alles”
   - Robust category match (normalize)
   - Spotlight hover/tap
   - EN toggle (data-i18n-nl/en + placeholders)
   - Quick view modal
   - Sticky mobile progress bar in Step3
   - Step3 category scroller (horizontal) to quickly switch filters while selecting products
   - Add to cart with multiple items + cart attributes for gift selections
*/

(() => {
  const root = document.getElementById('mix-match');
  if (!root) return;

  // ========= Helpers =========
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

  function normalize(s) {
    return (s ?? '').toString().trim().toLowerCase();
  }

  function formatMoneyFromCents(cents) {
    // Uses locale-ish formatting; Shopify will format on server, but we need UI format.
    const v = (Number(cents) || 0) / 100;
    // NL style by default; if EN selected, use en-GB style with € symbol
    const isEN = state.lang === 'en';
    try {
      return new Intl.NumberFormat(isEN ? 'en-GB' : 'nl-NL', {
        style: 'currency',
        currency: 'EUR'
      }).format(v);
    } catch (e) {
      return `€${v.toFixed(2)}`;
    }
  }

  function parseBudgetToCents(inputValue) {
    if (inputValue == null) return 0;
    const s = inputValue.toString().trim().replace(',', '.');
    const n = Number(s);
    if (!isFinite(n) || n <= 0) return 0;
    return Math.round(n * 100);
  }

  async function postJSON(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body)
    });
    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      throw new Error(`Request failed: ${res.status} ${txt}`);
    }
    return res.json().catch(() => ({}));
  }

  function getHeaderOffset() {
    const candidates = [
      document.querySelector('header'),
      document.querySelector('.header-wrapper'),
      document.querySelector('.shopify-section-header'),
      document.querySelector('#shopify-section-header')
    ].filter(Boolean);

    let h = 0;
    candidates.forEach(el => { h = Math.max(h, el.offsetHeight || 0); });

    const ann = document.querySelector('.announcement-bar') || document.querySelector('.utility-bar');
    if (ann) h += ann.offsetHeight || 0;

    // some themes have extra sticky padding
    return h;
  }

  function scrollToEl(el) {
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.pageYOffset - getHeaderOffset() - 12;
    window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
  }

  function autoScrollStep1Once() {
    if (window.location.hash) return;
    if (sessionStorage.getItem('mm_autoscroll_done') === '1') return;
    sessionStorage.setItem('mm_autoscroll_done', '1');

    const top = root.getBoundingClientRect().top + window.pageYOffset;
    const target = Math.max(0, top - getHeaderOffset() - 12);
    setTimeout(() => window.scrollTo({ top: target, behavior: 'auto' }), 60);
  }

  // ========= DOM =========
  const minItems = Number(root.dataset.minItems || root.dataset.min_items || 3) || 3;

  // Steps
  const step1 = $('#mm-step1', root);
  const step2 = $('#mm-step2', root);
  const step3 = $('#mm-step3', root);

  // Step1
  const purposeBtns = $$('.mm__purpose-btn', root);
  const goStep2Btn = $('#mm-go-step2', root);
  const step1Hint = $('#mm-step1-hint', root);

  const langToggle = $('#mm-lang-en', root);
  const budgetInput = $('#mm-budget-input', root);
  const budgetFillTop = $('#mm-budget-fill-top', root);
  const budgetMetaTop = $('#mm-budget-meta-top', root);

  // Step2
  const catList = $('#mm-catlist', root);
  const catItems = catList ? $$('.mm__catitem', catList) : [];
  const allCatsBtn = $('#mm-cat-all', root);
  const searchInput = $('#mm-search', root);
  const goStep3Btn = $('#mm-go-step3', root);
  const step2Hint = $('#mm-step2-hint', root);
  const spotlightSticky = $('#mm-spotlight-sticky', root);
  const spotlightHero = $('#mm-spotlight', root);

  // Step3 / addons
  const giftPack = $('#mm-giftpack', root);
  const cardPick = $('#mm-cardpick', root);
  const goCardBtn = $('#mm-go-card', root);
  const goProductsBtn = $('#mm-go-products', root);

  // Products + summary
  const layout = $('.mm__layout', root);
  const grid = $('#mm-grid', root);
  const cards = grid ? $$('.mm__card', grid) : [];

  const summary = $('#mm-summary', root);
  const countEl = $('#mm-count', root);
  const linesEl = $('#mm-lines', root);
  const totalEl = $('#mm-total', root);
  const addToCartBtn = $('#mm-addtocart', root);
  const hintEl = $('#mm-hint', root);

  const budgetFillSide = $('#mm-budget-fill-side', root);
  const budgetMetaSide = $('#mm-budget-meta-side', root);
  const budgetStatus = $('#mm-budget-status', root);

  // Modal
  const modal = $('#mm-modal', root);
  const modalImg = $('#mm-modal-img', root);
  const modalTitle = $('#mm-modal-title', root);
  const modalPrice = $('#mm-modal-price', root);
  const modalDesc = $('#mm-modal-desc', root);
  const modalLink = $('#mm-modal-link', root);
  const modalAdd = $('#mm-modal-add', root);

  // ========= State =========
  const state = {
    lang: 'nl', // nl | en
    purpose: null, // 'self' | 'gift'
    budgetCents: 0,
    allSelected: false,
    cats: new Set(), // normalized category keys
    search: '',
    selection: new Map(), // variantId -> {qty, title, priceCents, image, url}
    modalVariantId: null,

    gift: {
      pack: null, // {key,titleNL,titleEN,variantId,priceCents}
      card: null  // same
    }
  };

  // ========= i18n =========
  function applyI18n() {
    const isEN = state.lang === 'en';
    // Text nodes
    $$('[data-i18n-nl]', root).forEach(el => {
      const nl = el.getAttribute('data-i18n-nl');
      const en = el.getAttribute('data-i18n-en');
      if (nl == null && en == null) return;
      el.textContent = isEN ? (en ?? nl ?? '') : (nl ?? en ?? '');
    });
    // Placeholders
    $$('[data-placeholder-nl]', root).forEach(el => {
      const nl = el.getAttribute('data-placeholder-nl') || '';
      const en = el.getAttribute('data-placeholder-en') || nl;
      el.setAttribute('placeholder', isEN ? en : nl);
    });
    // Category titles/desc inside buttons (your earlier issue: JS can override to blank if attrs missing)
    catItems.forEach(btn => {
      const t = btn.querySelector('.mm__cattitle');
      const d = btn.querySelector('.mm__catdesc');
      const nlLabel = btn.getAttribute('data-label-nl') || t?.textContent || '';
      const enLabel = btn.getAttribute('data-label-en') || nlLabel;
      const nlDesc = btn.getAttribute('data-desc-nl') || d?.textContent || '';
      const enDesc = btn.getAttribute('data-desc-en') || nlDesc;
      if (t) t.textContent = isEN ? enLabel : nlLabel;
      if (d) d.textContent = isEN ? enDesc : nlDesc;
    });

    // Gift add-on tiles titles (if present in dataset)
    $$('.mm__addon-option', root).forEach(opt => {
      const name = opt.querySelector('.mm__addon-name');
      if (!name) return;
      const nl = opt.getAttribute('data-title-nl') || name.textContent || '';
      const en = opt.getAttribute('data-title-en') || nl;
      name.textContent = isEN ? en : nl;
      // “Gratis/Free” handled by data-i18n on span if present
      const freeSpan = opt.querySelector('.mm__addon-price[data-i18n-nl]');
      if (freeSpan) {
        const nl2 = freeSpan.getAttribute('data-i18n-nl') || 'Gratis';
        const en2 = freeSpan.getAttribute('data-i18n-en') || 'Free';
        freeSpan.textContent = isEN ? en2 : nl2;
      }
    });

    // Budget status text
    renderBudgetStatus();
    renderMobileBar();
  }

  // ========= Category UI / selection =========
  function setCatSelected(btn, on) {
    btn.classList.toggle('is-selected', !!on);
    btn.setAttribute('aria-pressed', on ? 'true' : 'false');
  }

  function selectedCatsCount() {
    return state.allSelected ? 999 : state.cats.size;
  }

  function refreshCategoryUI() {
    if (!catItems.length) return;
    catItems.forEach(btn => {
      const key = normalize(btn.dataset.cat || '');
      setCatSelected(btn, state.allSelected ? true : state.cats.has(key));
    });
    if (allCatsBtn) allCatsBtn.classList.toggle('is-selected', !!state.allSelected);
    updateStep3ButtonState();
    applyGridVisibility();
    syncStep3CatScroller();
  }

  function updateStep3ButtonState() {
    if (!goStep3Btn) return;
    const ok = state.allSelected || state.cats.size > 0;
    goStep3Btn.disabled = !ok;
    if (step2Hint) step2Hint.style.opacity = ok ? '0.6' : '1';
  }

  function updateStep2ButtonState() {
    if (!goStep2Btn) return;
    const ok = !!state.purpose;
    goStep2Btn.disabled = !ok;
    if (step1Hint) step1Hint.style.opacity = ok ? '0.6' : '1';
  }

  function updateSpotlight(src) {
    const img = spotlightSticky || spotlightHero;
    if (!img || !src) return;
    // Avoid flicker: only change if different
    if (img.getAttribute('src') !== src) img.setAttribute('src', src);
  }

  // ========= Product filtering =========
  function applyGridVisibility() {
    if (!grid) return;

    const q = normalize(state.search);
    const cardEls = $$('.mm__card', grid);

    cardEls.forEach(card => {
      const cat = normalize(card.dataset.category || '');
      const title = normalize(card.dataset.title || '');

      const byCat =
        (state.allSelected || state.cats.size === 0)
          ? true
          : state.cats.has(cat);

      const bySearch = !q || title.includes(q);

      card.style.display = (byCat && bySearch) ? '' : 'none';
    });
  }

  // ========= Selection + summary =========
  function addVariantToSelection(variantId, meta) {
    if (!variantId) return;
    const k = String(variantId);
    const existing = state.selection.get(k);
    if (existing) {
      existing.qty += 1;
      state.selection.set(k, existing);
    } else {
      state.selection.set(k, {
        qty: 1,
        title: meta.title || '',
        priceCents: Number(meta.priceCents || 0),
        image: meta.image || '',
        url: meta.url || ''
      });
    }
    renderSummary();
  }

  function removeVariantFromSelection(variantId) {
    const k = String(variantId);
    const existing = state.selection.get(k);
    if (!existing) return;
    if (existing.qty > 1) {
      existing.qty -= 1;
      state.selection.set(k, existing);
    } else {
      state.selection.delete(k);
    }
    renderSummary();
  }

  function computeTotals() {
    let qty = 0;
    let totalCents = 0;
    state.selection.forEach(item => {
      qty += item.qty;
      totalCents += (Number(item.priceCents) || 0) * item.qty;
    });
    return { qty, totalCents };
  }

  function renderLines() {
    if (!linesEl) return;
    linesEl.innerHTML = '';
    state.selection.forEach((item, variantId) => {
      const row = document.createElement('div');
      row.className = 'mm__line';

      const left = document.createElement('div');
      left.className = 'mm__line-left';

      const title = document.createElement('div');
      title.className = 'mm__line-title';
      title.textContent = item.title;

      const meta = document.createElement('div');
      meta.className = 'mm__line-meta';
      meta.textContent = `${item.qty} × ${formatMoneyFromCents(item.priceCents)}`;

      left.appendChild(title);
      left.appendChild(meta);

      const right = document.createElement('div');
      right.className = 'mm__line-right';

      const minus = document.createElement('button');
      minus.type = 'button';
      minus.className = 'mm__line-btn';
      minus.textContent = '−';
      minus.addEventListener('click', () => removeVariantFromSelection(variantId));

      const plus = document.createElement('button');
      plus.type = 'button';
      plus.className = 'mm__line-btn';
      plus.textContent = '+';
      plus.addEventListener('click', () => addVariantToSelection(variantId, item));

      right.appendChild(minus);
      right.appendChild(plus);

      row.appendChild(left);
      row.appendChild(right);

      linesEl.appendChild(row);
    });
  }

  function renderTotals() {
    const { qty, totalCents } = computeTotals();

    if (countEl) countEl.textContent = String(qty);
    if (totalEl) totalEl.textContent = formatMoneyFromCents(totalCents);

    const canCheckout = qty >= minItems;
    if (addToCartBtn) addToCartBtn.disabled = !canCheckout;

    if (hintEl) {
      const isEN = state.lang === 'en';
      if (canCheckout) {
        hintEl.style.opacity = '0.6';
        hintEl.textContent = isEN ? 'Ready to add to your cart.' : 'Klaar om toe te voegen aan je winkelwagen.';
      } else {
        hintEl.style.opacity = '1';
        hintEl.textContent = isEN
          ? `Select at least ${minItems} items to continue.`
          : `Selecteer minimaal ${minItems} stuks om door te gaan.`;
      }
    }

    renderBudgetUI();
  }

  function renderSummary() {
    renderLines();
    renderTotals();
  }

  // ========= Budget UI =========
  function renderBudgetStatus() {
    if (!budgetStatus) return;
    const { totalCents } = computeTotals();
    const b = state.budgetCents;

    const isEN = state.lang === 'en';
    if (!b) {
      budgetStatus.textContent = '';
      return;
    }
    if (totalCents <= b) {
      budgetStatus.textContent = isEN ? 'Within budget' : 'Binnen budget';
    } else {
      budgetStatus.textContent = isEN ? 'Over budget (allowed)' : 'Boven budget (mag)';
    }
  }

  function renderBudgetUI() {
    const { totalCents } = computeTotals();
    const b = state.budgetCents;

    const setFill = (el) => {
      if (!el) return;
      const pct = b ? clamp((totalCents / b) * 100, 0, 100) : 0;
      el.style.width = `${pct}%`;
    };

    setFill(budgetFillTop);
    setFill(budgetFillSide);

    const isEN = state.lang === 'en';
    const metaText = () => {
      if (!b) {
        const topNL = budgetMetaTop?.getAttribute('data-i18n-nl');
        const topEN = budgetMetaTop?.getAttribute('data-i18n-en');
        return isEN ? (topEN || 'Enter a target budget to see your progress.') : (topNL || 'Vul een richtprijs in om je voortgang te zien.');
      }
      return isEN
        ? `Total ${formatMoneyFromCents(totalCents)} / Budget ${formatMoneyFromCents(b)}`
        : `Totaal ${formatMoneyFromCents(totalCents)} / Budget ${formatMoneyFromCents(b)}`;
    };

    if (budgetMetaTop) budgetMetaTop.textContent = metaText();
    if (budgetMetaSide) budgetMetaSide.textContent = metaText();

    renderBudgetStatus();
    renderMobileBar();
  }

  // ========= Sticky Mobile Bar (Step3) =========
  let mobileBar = null;
  let mobileBarFill = null;
  let mobileBarText = null;

  function ensureMobileBar() {
    if (mobileBar) return;
    mobileBar = document.createElement('div');
    mobileBar.id = 'mm-mobilebar';
    mobileBar.setAttribute('aria-label', 'Mix & Match voortgang');

    // Inline styles so you don’t need extra CSS for this feature
    mobileBar.style.position = 'sticky';
    mobileBar.style.top = `${getHeaderOffset()}px`;
    mobileBar.style.zIndex = '50';
    mobileBar.style.background = '#fff';
    mobileBar.style.borderBottom = '1px solid rgba(0,0,0,.08)';
    mobileBar.style.padding = '10px 14px';
    mobileBar.style.display = 'none';

    const row = document.createElement('div');
    row.style.display = 'flex';
    row.style.gap = '12px';
    row.style.alignItems = 'center';
    row.style.justifyContent = 'space-between';

    mobileBarText = document.createElement('div');
    mobileBarText.style.fontSize = '13px';
    mobileBarText.style.lineHeight = '1.2';
    mobileBarText.style.flex = '1';

    const bar = document.createElement('div');
    bar.style.height = '8px';
    bar.style.borderRadius = '999px';
    bar.style.background = 'rgba(0,0,0,.08)';
    bar.style.flex = '1.2';
    bar.style.overflow = 'hidden';

    mobileBarFill = document.createElement('div');
    mobileBarFill.style.height = '100%';
    mobileBarFill.style.width = '0%';
    mobileBarFill.style.background = 'rgba(0,0,0,.55)';

    bar.appendChild(mobileBarFill);
    row.appendChild(mobileBarText);
    row.appendChild(bar);
    mobileBar.appendChild(row);

    // Insert at top of Step3 so it sticks while selecting products
    const container = $('.mm__container', step3) || step3;
    container.insertBefore(mobileBar, container.firstChild);
  }

  function renderMobileBar() {
    if (!step3) return;
    ensureMobileBar();

    const isMobile = window.matchMedia('(max-width: 989px)').matches;
    if (!isMobile) {
      mobileBar.style.display = 'none';
      return;
    }

    // Only show when Step3 is visible (in view)
    const rect = step3.getBoundingClientRect();
    const inView = rect.top < window.innerHeight * 0.25 && rect.bottom > window.innerHeight * 0.25;
    mobileBar.style.display = inView ? 'block' : 'none';

    const { qty, totalCents } = computeTotals();
    const b = state.budgetCents;

    const isEN = state.lang === 'en';
    const left = isEN ? `Selected: ${qty}/${minItems}+` : `Gekozen: ${qty}/${minItems}+`;
    const right = b ? `${formatMoneyFromCents(totalCents)} / ${formatMoneyFromCents(b)}` : `${formatMoneyFromCents(totalCents)}`;

    mobileBarText.textContent = `${left} • ${right}`;

    const pct = b ? clamp((totalCents / b) * 100, 0, 100) : 0;
    mobileBarFill.style.width = `${pct}%`;
    mobileBar.style.top = `${getHeaderOffset()}px`;
  }

  window.addEventListener('scroll', () => renderMobileBar(), { passive: true });
  window.addEventListener('resize', () => renderMobileBar());

  // ========= Step3 Category Scroller (mobile + desktop) =========
  let scroller = null;

  function buildStep3CatScroller() {
    if (!step3) return;
    if (scroller) return;

    scroller = document.createElement('div');
    scroller.id = 'mm-step3-cats';

    // Inline styles
    scroller.style.display = 'flex';
    scroller.style.gap = '8px';
    scroller.style.overflowX = 'auto';
    scroller.style.padding = '8px 0';
    scroller.style.margin = '10px 0 18px';
    scroller.style.scrollbarWidth = 'none'; // firefox
    scroller.style.webkitOverflowScrolling = 'touch';

    const container = $('.mm__container', step3) || step3;
    const anchor = container.querySelector('.mm__layout') || container.lastChild;
    container.insertBefore(scroller, anchor);

    const addChip = (label, key) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'mm__chip';
      btn.style.whiteSpace = 'nowrap';
      btn.dataset.key = key;
      btn.textContent = label;
      btn.addEventListener('click', () => {
        if (key === '__all__') {
          state.allSelected = true;
          state.cats.clear();
        } else {
          state.allSelected = false;
          if (state.cats.has(key)) state.cats.delete(key);
          else state.cats.add(key);
        }
        refreshCategoryUI();
      });
      scroller.appendChild(btn);
    };

    // Build initial chips from step2 list (so titles are consistent)
    scroller.innerHTML = '';
    const isEN = state.lang === 'en';

    // All chip
    const allLabel = allCatsBtn ? allCatsBtn.textContent.trim() : (isEN ? 'Select all' : 'Alles selecteren');
    addChip(allLabel, '__all__');

    catItems.forEach(btn => {
      const key = normalize(btn.dataset.cat || '');
      const label = isEN
        ? (btn.getAttribute('data-label-en') || btn.textContent.trim())
        : (btn.getAttribute('data-label-nl') || btn.textContent.trim());
      addChip(label, key);
    });

    syncStep3CatScroller();
  }

  function syncStep3CatScroller() {
    if (!scroller) return;
    const chips = Array.from(scroller.querySelectorAll('button.mm__chip'));
    chips.forEach(ch => {
      const key = ch.dataset.key;
      const on = (key === '__all__') ? state.allSelected : (!state.allSelected && state.cats.has(key));
      ch.classList.toggle('is-selected', !!on);
    });
  }

  // ========= Gift add-ons (packaging + cards) =========
  function isGiftFlow() {
    return state.purpose === 'gift';
  }

  function resetGiftFlow() {
    state.gift.pack = null;
    state.gift.card = null;
    // UI reset
    $$('.mm__addon-option.is-selected', root).forEach(el => el.classList.remove('is-selected'));
    if (goCardBtn) goCardBtn.disabled = true;
    if (goProductsBtn) goProductsBtn.disabled = true;
  }

  function showGiftFlowIfNeeded() {
    if (!giftPack || !cardPick) return;
    if (!layout) return;

    if (isGiftFlow()) {
      // Show packaging first, hide cards + products until completed
      giftPack.hidden = false;
      cardPick.hidden = true;
      layout.style.display = 'none';
      resetGiftFlow();
    } else {
      // Hide addons, show products
      giftPack.hidden = true;
      cardPick.hidden = true;
      layout.style.display = '';
      resetGiftFlow();
    }
  }

  function selectAddon(kind, optEl) {
    if (!optEl) return;

    // Deselect all within that addon block
    const wrap = optEl.closest('.mm__addon');
    if (wrap) $$('.mm__addon-option', wrap).forEach(o => o.classList.remove('is-selected'));
    optEl.classList.add('is-selected');

    const key = optEl.dataset.addonKey || '';
    const titleNL = optEl.getAttribute('data-title-nl') || optEl.querySelector('.mm__addon-name')?.textContent || '';
    const titleEN = optEl.getAttribute('data-title-en') || titleNL;
    const variantId = String(optEl.dataset.variantId || '0');
    const priceCents = Number(optEl.dataset.priceCents || 0) || 0;

    state.gift[kind] = { key, titleNL, titleEN, variantId, priceCents };

    if (kind === 'pack' && goCardBtn) goCardBtn.disabled = false;
    if (kind === 'card' && goProductsBtn) goProductsBtn.disabled = false;
  }

  function showCardsStep() {
    if (!giftPack || !cardPick) return;
    giftPack.hidden = true;
    cardPick.hidden = false;
    if (layout) layout.style.display = 'none';
    scrollToEl(cardPick);
  }

  function showProductsStep() {
    if (!giftPack || !cardPick) return;
    giftPack.hidden = true;
    cardPick.hidden = true;
    if (layout) layout.style.display = '';
    buildStep3CatScroller();
    scrollToEl(layout);
    renderMobileBar();
  }

  // ========= Modal =========
  function openModalFromCard(cardEl) {
    if (!modal || !cardEl) return;
    const variantId = cardEl.dataset.variantId;
    state.modalVariantId = variantId;

    if (modalTitle) modalTitle.textContent = cardEl.dataset.title || '';
    if (modalPrice) modalPrice.textContent = formatMoneyFromCents(cardEl.dataset.priceCents);
    if (modalDesc) modalDesc.textContent = cardEl.dataset.desc || '';
    if (modalLink) modalLink.href = cardEl.dataset.url || '#';

    const img = cardEl.dataset.image || '';
    if (modalImg) {
      modalImg.src = img;
      modalImg.alt = cardEl.dataset.title || '';
    }

    modal.setAttribute('aria-hidden', 'false');
    modal.classList.add('is-open');
    document.documentElement.style.overflow = 'hidden';
  }

  function closeModal() {
    if (!modal) return;
    modal.setAttribute('aria-hidden', 'true');
    modal.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    state.modalVariantId = null;
  }

  // ========= Cart (Add items) =========
  async function addSelectionToCart() {
    const { qty } = computeTotals();
    if (qty < minItems) return;

    // Gift flow validation (extra safety)
    if (isGiftFlow()) {
      if (!state.gift.pack || !state.gift.card) {
        // Should not happen due to gating
        return;
      }
    }

    const items = [];

    // Add selected products (with qty)
    state.selection.forEach((item, variantId) => {
      items.push({
        id: Number(variantId),
        quantity: item.qty,
        properties: {
          'Mix & Match': 'Yes'
        }
      });
    });

    // Add paid packaging/card as separate line items (variantId != 0)
    if (isGiftFlow()) {
      const pack = state.gift.pack;
      const card = state.gift.card;
      if (pack && pack.variantId && pack.variantId !== '0') {
        items.push({ id: Number(pack.variantId), quantity: 1, properties: { 'Mix & Match Add-on': 'Packaging' } });
      }
      if (card && card.variantId && card.variantId !== '0') {
        items.push({ id: Number(card.variantId), quantity: 1, properties: { 'Mix & Match Add-on': 'Card' } });
      }
    }

    // 1) Add items
    await postJSON('/cart/add.js', { items });

    // 2) Update cart attributes for gift selections (also for free options)
    const attrs = {};
    attrs['Mix & Match - Language'] = (state.lang === 'en') ? 'EN' : 'NL';

    if (isGiftFlow()) {
      const packTitle = state.lang === 'en' ? state.gift.pack?.titleEN : state.gift.pack?.titleNL;
      const cardTitle = state.lang === 'en' ? state.gift.card?.titleEN : state.gift.card?.titleNL;
      if (packTitle) attrs['Mix & Match - Packaging'] = packTitle;
      if (cardTitle) attrs['Mix & Match - Card'] = cardTitle;
    } else {
      attrs['Mix & Match - Purpose'] = (state.lang === 'en') ? 'For myself' : 'Voor mezelf';
    }

    if (state.budgetCents > 0) {
      attrs['Mix & Match - Budget'] = formatMoneyFromCents(state.budgetCents);
    }

    await postJSON('/cart/update.js', { attributes: attrs });

    // 3) Go to cart
    window.location.href = '/cart';
  }

  // ========= Events =========
  // Step1 purpose
  purposeBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      const p = btn.dataset.purpose;
      state.purpose = p === 'gift' ? 'gift' : 'self';

      purposeBtns.forEach(b => b.classList.toggle('is-selected', b === btn));
      updateStep2ButtonState();

      // If user already opened step3 and changes purpose, we must update gift flow visibility
      showGiftFlowIfNeeded();
    });
  });

  // EN toggle
  if (langToggle) {
    langToggle.addEventListener('change', () => {
      state.lang = langToggle.checked ? 'en' : 'nl';
      applyI18n();
      // rebuild scroller labels if already built
      if (scroller) {
        scroller.remove();
        scroller = null;
        if (!step3.hidden) buildStep3CatScroller();
      }
    });
  }

  // Budget input
  if (budgetInput) {
    budgetInput.addEventListener('input', () => {
      state.budgetCents = parseBudgetToCents(budgetInput.value);
      renderBudgetUI();
    });
  }

  // Step1 -> Step2
  if (goStep2Btn) {
    goStep2Btn.addEventListener('click', () => {
      if (!state.purpose) return;
      if (step2) step2.hidden = false;
      scrollToEl(step2);
    });
  }

  // Categories
  if (allCatsBtn) {
    allCatsBtn.addEventListener('click', () => {
      state.allSelected = true;
      state.cats.clear();
      refreshCategoryUI();
    });
  }

  catItems.forEach(btn => {
    const key = normalize(btn.dataset.cat || '');

    // Hover/focus spotlight
    const spotlight = btn.dataset.spotlight;
    if (spotlight) {
      btn.addEventListener('mouseenter', () => updateSpotlight(spotlight));
      btn.addEventListener('focus', () => updateSpotlight(spotlight));
      // mobile: also on touch/click we update spotlight
    }

    btn.addEventListener('click', () => {
      // selection
      state.allSelected = false;
      if (state.cats.has(key)) state.cats.delete(key);
      else state.cats.add(key);

      // update spotlight on tap
      if (spotlight) updateSpotlight(spotlight);

      refreshCategoryUI();
    });
  });

  // Search (filters products by title)
  if (searchInput) {
    searchInput.addEventListener('input', () => {
      state.search = searchInput.value || '';
      applyGridVisibility();
    });
  }

  // Step2 -> Step3
  if (goStep3Btn) {
    goStep3Btn.addEventListener('click', () => {
      const ok = state.allSelected || state.cats.size > 0;
      if (!ok) return;

      if (step3) step3.hidden = false;
      scrollToEl(step3);

      showGiftFlowIfNeeded();
      buildStep3CatScroller();

      applyGridVisibility();
      renderMobileBar();
    });
  }

  // Gift add-on selection
  if (giftPack) {
    $$('.mm__addon-option', giftPack).forEach(opt => {
      opt.addEventListener('click', () => selectAddon('pack', opt));
    });
  }
  if (cardPick) {
    $$('.mm__addon-option', cardPick).forEach(opt => {
      opt.addEventListener('click', () => selectAddon('card', opt));
    });
  }

  // Packaging -> Cards
  if (goCardBtn) {
    goCardBtn.addEventListener('click', () => {
      if (!state.gift.pack) return;
      showCardsStep();
    });
  }

  // Cards -> Products
  if (goProductsBtn) {
    goProductsBtn.addEventListener('click', () => {
      if (!state.gift.card) return;
      showProductsStep();
    });
  }

  // Product card actions (event delegation)
  if (grid) {
    grid.addEventListener('click', (e) => {
      const btn = e.target.closest('button');
      const card = e.target.closest('.mm__card');
      if (!card) return;

      // info button
      if (btn && btn.dataset.action === 'info') {
        openModalFromCard(card);
        return;
      }

      // add button
      if (btn && btn.dataset.action === 'add') {
        const available = card.dataset.available === '1';
        if (!available) return;

        addVariantToSelection(card.dataset.variantId, {
          title: card.dataset.title,
          priceCents: Number(card.dataset.priceCents || 0),
          image: card.dataset.image,
          url: card.dataset.url
        });
        return;
      }
    });
  }

  // Modal close + add
  if (modal) {
    modal.addEventListener('click', (e) => {
      const a = e.target.closest('[data-action]');
      if (!a) return;
      if (a.dataset.action === 'close') closeModal();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeModal();
    });
  }

  if (modalAdd) {
    modalAdd.addEventListener('click', () => {
      if (!state.modalVariantId) return;
      // Find card meta from grid
      const card = grid ? grid.querySelector(`.mm__card[data-variant-id="${CSS.escape(String(state.modalVariantId))}"]`) : null;
      if (!card) return;
      const available = card.dataset.available === '1';
      if (!available) return;

      addVariantToSelection(card.dataset.variantId, {
        title: card.dataset.title,
        priceCents: Number(card.dataset.priceCents || 0),
        image: card.dataset.image,
        url: card.dataset.url
      });
      closeModal();
    });
  }

  // Add to cart
  if (addToCartBtn) {
    addToCartBtn.addEventListener('click', async () => {
      try {
        addToCartBtn.disabled = true;
        await addSelectionToCart();
      } catch (err) {
        console.error(err);
        // re-enable so user can retry
        renderTotals();
        alert(state.lang === 'en'
          ? 'Something went wrong while adding to cart. Please try again.'
          : 'Er ging iets mis bij het toevoegen aan de winkelwagen. Probeer het opnieuw.');
      }
    });
  }

  // ========= Initial =========
  autoScrollStep1Once();
  updateStep2ButtonState();
  refreshCategoryUI();
  applyGridVisibility();
  renderSummary();
  renderBudgetUI();
  applyI18n();

  // If step2/3 were already visible due to theme editor preview, ensure correct view
  if (step2 && !step2.hidden) scrollToEl(step2);
  if (step3 && !step3.hidden) {
    showGiftFlowIfNeeded();
    buildStep3CatScroller();
    renderMobileBar();
  }
})();
